import React, { useCallback, useEffect, useRef } from 'react';
import { AudioLines } from 'lucide-react';
import { SpeakerSegment } from '../../types';
import { SPEAKER_ME, type PendingLine } from './useRecorder';
import { displaySpeaker } from './speakerLabels';
import { t } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';

/** Kullanıcı elle yukarı kaydırdıysa otomatik takip bırakılır. */
const STICK_THRESHOLD_PX = 90;

function speakerClass(speaker: string): string {
  return speaker === SPEAKER_ME ? 'text-[#46CBE2]' : 'text-amber-400';
}

interface LiveTranscriptStreamProps {
  segments: SpeakerSegment[];
  pendingLines: PendingLine[];
  isPaused: boolean;
  isStarting: boolean;
  isDarkMode: boolean;
}

/**
 * Canlı transkript akışı — konuşulan cümleler tamamlandıkça teker teker
 * düşüyor.
 *
 * İki tür satır var: tamamlanmış cümleler (kalıcı) ve oluşmakta olan cümle
 * (soluk, imleçli). Ayrım Deepgram'dan geliyor: ara sonuçlar soluk satırda
 * akıyor, cümle sonu (`speech_final` / `UtteranceEnd`) gelince kalıcı satıra
 * dönüşüyor (bkz. useRecorder.ts).
 */
export const LiveTranscriptStream: React.FC<LiveTranscriptStreamProps> = ({
  segments,
  pendingLines,
  isPaused,
  isStarting,
  isDarkMode,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < STICK_THRESHOLD_PX;
  }, []);

  // Yeni satır geldiğinde aşağı kaydır — ama kullanıcı geçmişi okumak için
  // yukarı çıktıysa onu geri çekme.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [segments, pendingLines]);

  const isEmpty = segments.length === 0 && pendingLines.length === 0;

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      /**
       * Boşken kap bir flex kutusuna dönüşüp mesajı ortalıyor. Eskiden mesaj
       * `py-20` ile üste yapışıyor, altında kalan yükseklik boyunca sayfa
       * arka planı çıplak duruyordu — kayıt ekranının altındaki "gereksiz
       * boşluk" hissi buradan geliyordu.
       */
      className={`flex-1 min-h-0 overflow-y-auto px-1 pt-5 ${
        isEmpty ? 'flex items-center justify-center pb-5' : 'pb-6'
      }`}
    >
      {isEmpty && (
        <div className="flex flex-col items-center text-center gap-3.5 text-neutral-500 -mt-6">
          <span
            className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
              isDarkMode ? 'bg-[#19191C]' : 'bg-neutral-100'
            }`}
          >
            <AudioLines className={`w-5 h-5 ${isPaused || isStarting ? '' : 'animate-pulse'}`} />
          </span>
          <p className="text-[13.5px] leading-relaxed max-w-[19rem]">
            {isStarting
              ? IS_WEB
                ? t('Mikrofon açılıyor…', 'Starting the microphone…')
                : t('Mikrofon ve sistem sesi açılıyor…', 'Starting the microphone and system audio…')
              : isPaused
                ? t(
                    'Duraklatıldı — devam ettiğinizde cümleler buraya düşmeye devam edecek.',
                    'Paused — sentences will keep appearing here when you resume.'
                  )
                : t(
                    'Konuşmaya başlayın. Cümleler tamamlandıkça teker teker buraya düşecek.',
                    'Start talking. Each sentence will appear here as it’s completed.'
                  )}
          </p>
        </div>
      )}

      <div className={isEmpty ? 'hidden' : 'space-y-3.5'}>
        {segments.map((segment) => (
          <div key={segment.id} className="animate-transcript-in flex items-baseline gap-3 group">
            <span
              className={`shrink-0 text-[11px] font-semibold uppercase tracking-wide w-16 text-right ${speakerClass(segment.speaker)}`}
            >
              {displaySpeaker(segment.speaker)}
            </span>
            <p
              className={`text-[17px] leading-relaxed ${
                isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'
              }`}
            >
              {segment.text}
            </p>
            <span className="ml-auto shrink-0 text-[11px] font-mono text-neutral-600 opacity-0 group-hover:opacity-100 transition-opacity">
              {segment.timestamp}
            </span>
          </div>
        ))}

        {/* Oluşmakta olan cümleler — bir sonraki parçayla tamamlanınca
            yukarıdaki kalıcı listeye geçiyorlar. */}
        {pendingLines.map((line) => (
          <div key={`pending-${line.speaker}`} className="flex items-baseline gap-3">
            <span
              className={`shrink-0 text-[11px] font-semibold uppercase tracking-wide w-16 text-right opacity-50 ${speakerClass(line.speaker)}`}
            >
              {displaySpeaker(line.speaker)}
            </span>
            <p className="text-[17px] leading-relaxed text-neutral-500">
              {line.text}
              <span className="inline-block w-[2px] h-[15px] ml-1 align-middle bg-neutral-500 animate-pulse" />
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
